"use client";

import { useMemo, useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

type Category =
  | "school"
  | "technology"
  | "society"
  | "environment"
  | "ethics"
  | "politics";

type Difficulty = "middle" | "high" | "college";

interface DebateTopic {
  topic: string;
  category: Category;
  difficulty: Difficulty;
}

/**
 * Iteration order for reassembling the flat topic pool from `strings.topics`.
 * Enum values, not translatable — display labels for these live in the strings
 * object (`school`, `technology`, … / `middleSchool`, `highSchool`, `college`).
 */
const CATEGORIES: Category[] = [
  "school",
  "technology",
  "society",
  "environment",
  "ethics",
  "politics",
];
const DIFFICULTIES: Difficulty[] = ["middle", "high", "college"];

function topicGroupKey(category: Category, difficulty: Difficulty): string {
  return `${category}:${difficulty}`;
}

function entries(
  category: Category,
  difficulty: Difficulty,
  topics: readonly string[]
): DebateTopic[] {
  return topics.map((topic) => ({ topic, category, difficulty }));
}

/** Reassembles the flat topic pool from the strings object's grouped lists. */
function buildTopics(
  topicGroups: Record<string, readonly string[]>
): DebateTopic[] {
  const result: DebateTopic[] = [];
  for (const category of CATEGORIES) {
    for (const difficulty of DIFFICULTIES) {
      const group = topicGroups[topicGroupKey(category, difficulty)] ?? [];
      result.push(...entries(category, difficulty, group));
    }
  }
  return result;
}

const RECENT_LIMIT = 8;

function pickTopic(
  pool: DebateTopic[],
  current: DebateTopic | null
): DebateTopic | null {
  if (pool.length === 0) {
    return null;
  }
  const candidates =
    current === null ? pool : pool.filter((t) => t.topic !== current.topic);
  if (candidates.length === 0) {
    return current;
  }
  return candidates[Math.floor(Math.random() * candidates.length)];
}

/** English strings. A translated content file overrides these through `ui`. */
export const DEBATE_TOPIC_GENERATOR_STRINGS = {
  category: "Category",
  difficulty: "Difficulty",
  allCategories: "All categories",
  school: "School",
  technology: "Technology",
  society: "Society",
  environment: "Environment",
  ethics: "Ethics",
  politicsCivic: "Politics (civic)",
  allLevels: "All levels",
  middleSchool: "Middle school",
  highSchool: "High school",
  college: "College",
  newTopic: "New Topic",
  topicWord: "one: topic|other: topics",
  matchYourFilters: " match your filters",
  copied: "Copied!",
  copy: "Copy",
  recentTopics: "Recent topics",
  topics: {
    "school:middle": [
      "Should students be allowed to use phones during school breaks?",
      "Should homework be limited to 30 minutes per night?",
      "Should schools require uniforms?",
      "Should recess be longer for middle schoolers?",
      "Should students get to grade their teachers?",
      "Should the school day start later in the morning?",
    ],
    "school:high": [
      "Should final exams be replaced with project-based assessments?",
      "Should high schools require a personal finance class?",
      "Should class rank and valedictorian honors be abolished?",
      "Should schools block social media on campus Wi-Fi?",
      "Should standardized tests be required for graduation?",
      "Should students be paid for good grades?",
    ],
    "school:college": [
      "Should tuition be free at public universities?",
      "Should attendance be optional in university courses?",
      "Should universities permanently drop standardized test requirements?",
      "Should college athletes be paid salaries?",
      "Should grade inflation be curbed with fixed grading curves?",
      "Should universities require a core curriculum for all majors?",
    ],
    "technology:middle": [
      "Should kids under 13 be allowed on social media?",
      "Should video games count as a school sport?",
      "Should tablets replace printed textbooks in classrooms?",
      "Should parents track their children's phone locations?",
      "Should robots ever be used to teach classes?",
      "Should there be a daily screen time limit for kids?",
    ],
    "technology:high": [
      "Should AI writing tools be allowed for school assignments?",
      "Should facial recognition be used in public schools?",
      "Should social media platforms be required to verify users' ages?",
      "Should self-driving cars be allowed on public roads?",
      "Should phone makers be required to make batteries replaceable?",
      "Should websites be required to offer an ad-free paid option?",
    ],
    "technology:college": [
      "Should governments regulate recommendation algorithms?",
      "Should AI-generated art be eligible for copyright?",
      "Should gene-editing technology be used to prevent hereditary disease?",
      "Should large tech companies be broken up under antitrust law?",
      "Should internet access be treated as a public utility?",
      "Should employers be allowed to monitor remote workers with software?",
    ],
    "society:middle": [
      "Should zoos exist?",
      "Should school cafeterias serve only healthy food?",
      "Should kids get paid for doing chores?",
      "Should towns enforce curfews for teenagers?",
      "Should everyone be required to learn a second language?",
      "Should junk food ads be banned during children's TV shows?",
    ],
    "society:high": [
      "Should the voting age be lowered to 16?",
      "Should community service be required for graduation?",
      "Should professional athletes' salaries be capped?",
      "Should tipping be replaced by higher base wages?",
      "Should media literacy be a required school subject?",
      "Should reality TV be considered harmful entertainment?",
    ],
    "society:college": [
      "Should a four-day workweek become the standard?",
      "Should jury duty be voluntary?",
      "Should museums return artifacts to their countries of origin?",
      "Should governments run universal basic income pilots?",
      "Should public transportation be fare-free in major cities?",
      "Should professional sports leagues ban gambling sponsorships?",
    ],
    "environment:middle": [
      "Should plastic straws be banned?",
      "Should schools go completely paperless?",
      "Should every school have a vegetable garden?",
      "Should schools serve a meat-free lunch once a week?",
      "Should bottled water be banned at school events?",
      "Should recycling be taught as a graded subject?",
    ],
    "environment:high": [
      "Should gas-powered leaf blowers be banned in cities?",
      "Should fast fashion brands pay an environmental tax?",
      "Should new homes be required to include solar panels?",
      "Should short-haul flights be replaced by trains where possible?",
      "Should national parks limit the number of visitors?",
      "Should schools be required to serve locally grown food?",
    ],
    "environment:college": [
      "Should nuclear power be central to climate policy?",
      "Should carbon emissions be taxed at the corporate level?",
      "Should cities ban private cars from downtown cores?",
      "Should deep-sea mining be prohibited until further research?",
      "Should countries be financially liable for cross-border pollution?",
      "Should geoengineering research receive public funding?",
    ],
    "ethics:middle": [
      "Is it ever okay to tell a white lie?",
      "Should students report friends who cheat?",
      "Is it fair for stores to charge more when demand is high?",
      "Should people be required to return lost money they find?",
      "Is competition good for friendships?",
      "Should animals be kept as classroom pets?",
    ],
    "ethics:high": [
      "Should animal testing be banned for cosmetics?",
      "Is it ethical to eat meat when alternatives exist?",
      "Should parents post photos of their children online?",
      "Is it fair to judge historical figures by today's standards?",
      "Should stores be allowed to use surge pricing?",
      "Should violent video games carry stricter age restrictions?",
    ],
    "ethics:college": [
      "Is civil disobedience justified in a democracy?",
      "Should organ donation be opt-out by default?",
      "Is it ethical to edit the genes of human embryos?",
      "Should free speech protections extend to hate speech?",
      "Is lying ever morally required?",
      "Should wealthy nations be obligated to accept climate refugees?",
    ],
    "politics:middle": [
      "Should class presidents have real decision-making power?",
      "Should voting in school elections be mandatory?",
      "Should students help write the school rules?",
      "Should towns spend more on parks or on libraries?",
      "Should school boards include student members?",
      "Should communities vote on how to spend part of the local budget?",
    ],
    "politics:high": [
      "Should election day be a national holiday?",
      "Should there be term limits for members of Congress?",
      "Should the minimum wage be raised?",
      "Should political ads be banned on social media?",
      "Should the driving age be raised to 18?",
      "Should civics be a required course for graduation?",
    ],
    "politics:college": [
      "Should the electoral college be replaced by a national popular vote?",
      "Should campaign donations be capped at a low fixed amount?",
      "Should voting be compulsory, as it is in Australia?",
      "Should Supreme Court justices have term limits?",
      "Should ranked-choice voting be adopted nationwide?",
      "Should former legislators be banned from lobbying?",
    ],
  },
} as const;

export default function DebateTopicGeneratorWidget() {
  const [category, setCategory] = useState<Category | "all">("all");
  const [difficulty, setDifficulty] = useState<Difficulty | "all">("all");
  const [current, setCurrent] = useState<DebateTopic | null>(null);
  const [recent, setRecent] = useState<DebateTopic[]>([]);
  const [copied, setCopied] = useState(false);

  const locale = useToolLocale();
  const strings = useToolUi(DEBATE_TOPIC_GENERATOR_STRINGS);

  const categoryOptions: { value: Category | "all"; label: string }[] = [
    { value: "all", label: strings.allCategories },
    { value: "school", label: strings.school },
    { value: "technology", label: strings.technology },
    { value: "society", label: strings.society },
    { value: "environment", label: strings.environment },
    { value: "ethics", label: strings.ethics },
    { value: "politics", label: strings.politicsCivic },
  ];
  const difficultyOptions: { value: Difficulty | "all"; label: string }[] = [
    { value: "all", label: strings.allLevels },
    { value: "middle", label: strings.middleSchool },
    { value: "high", label: strings.highSchool },
    { value: "college", label: strings.college },
  ];
  const difficultyLabels: Record<Difficulty, string> = {
    middle: strings.middleSchool,
    high: strings.highSchool,
    college: strings.college,
  };
  const categoryLabels: Record<Category, string> = {
    school: strings.school,
    technology: strings.technology,
    society: strings.society,
    environment: strings.environment,
    ethics: strings.ethics,
    politics: strings.politicsCivic,
  };

  const allTopics = useMemo(
    () => buildTopics(strings.topics),
    [strings.topics]
  );

  const pool = allTopics.filter((t) => {
    const categoryMatch = category === "all" || t.category === category;
    const difficultyMatch = difficulty === "all" || t.difficulty === difficulty;
    return categoryMatch && difficultyMatch;
  });

  function handleNewTopic() {
    const next = pickTopic(pool, current);
    if (next === null) {
      return;
    }
    if (current !== null && current.topic !== next.topic) {
      setRecent((prev) =>
        [current, ...prev.filter((t) => t.topic !== current.topic)].slice(
          0,
          RECENT_LIMIT
        )
      );
    }
    setCurrent(next);
    setCopied(false);
  }

  function handleCopy() {
    if (current === null) {
      return;
    }
    writeToClipboard(current.topic).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Category filter */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.category}
        </p>
        <div className="flex flex-wrap gap-3">
          {categoryOptions.map((c) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={c.value}
            >
              <input
                checked={category === c.value}
                className="accent-primary"
                name="debate-category"
                onChange={() => setCategory(c.value)}
                type="radio"
                value={c.value}
              />
              {c.label}
            </label>
          ))}
        </div>
      </section>

      {/* Difficulty filter */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.difficulty}
        </p>
        <div className="flex flex-wrap gap-3">
          {difficultyOptions.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={difficulty === d.value}
                className="accent-primary"
                name="debate-difficulty"
                onChange={() => setDifficulty(d.value)}
                type="radio"
                value={d.value}
              />
              {d.label}
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={pool.length === 0}
          onClick={handleNewTopic}
          type="button"
        >
          {strings.newTopic}
        </button>
        <p className="text-muted-foreground text-xs">
          {pool.length}
          {tPlural(strings.topicWord, locale, pool.length)}
          {strings.matchYourFilters}
        </p>
      </div>

      {/* Current topic */}
      {current !== null && (
        <section className="space-y-3">
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="text-foreground text-lg leading-relaxed">
              {current.topic}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-border bg-muted/50 px-3 py-1 text-muted-foreground text-xs">
                {categoryLabels[current.category]}
              </span>
              <span className="rounded-full border border-border bg-muted/50 px-3 py-1 text-muted-foreground text-xs">
                {difficultyLabels[current.difficulty]}
              </span>
              <button
                className="ml-auto rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                onClick={handleCopy}
                type="button"
              >
                {copied ? strings.copied : strings.copy}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Recent topics */}
      {recent.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.recentTopics}
          </h2>
          <ul className="space-y-2">
            {recent.map((t) => (
              <li
                className="rounded-lg border border-border bg-card px-4 py-3 text-muted-foreground text-sm leading-relaxed"
                key={t.topic}
              >
                {t.topic}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
